import { service } from '@ember/service'
import ActiveModelAdapter from 'teacher-ui-client/adapters/active-model-adapter'
import config from 'teacher-ui-client/config/environment'
import { adapterHostAndNamespace } from 'teacher-ui-client/utils/uri'
import { pickBy } from 'lodash'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import AssignmentSerializer from 'teacher-ui-client/serializers/assignment'
import { getOwner } from '@ember/owner'
import { assert } from '@ember/debug'

const gravityV3Url = new URL(config.gravityV3Url)
export const { host: gravityV3Host, namespace: gravityV3Namespace } = adapterHostAndNamespace(gravityV3Url)
export const gravityV3BaseUrl = gravityV3Url.href

export default class AssignmentAdapter extends ActiveModelAdapter {
  @service authToken!: AuthToken

  // @ts-expect-error - parent defines this as a property, we override with getter
  get host() {
    return gravityV3Host
  }

  // @ts-expect-error - parent defines this as a property, we override with getter
  get namespace() {
    return gravityV3Namespace
  }

  // @ts-expect-error - parent defines this as a property, we override with getter
  get headers() {
    return {
      Authorization: this.authToken.token,
    }
  }

  /**
   * Use the prefix and suffix provided via queryparams to adjust the Assignments API endpoint
   * Converts
   * /assignments (with prefix: 'teacher/re' + suffix: 'current')
   * into
   * /assignments/teacher/re/current
   *
   * Prefix and suffix assumed not to start or end with /
   */
  urlForQuery(...args: Parameters<ActiveModelAdapter['urlForQuery']>): string {
    const [query] = args
    let prefix: unknown
    let suffix: unknown
    if ('prefix' in query) {
      prefix = query.prefix
      delete query.prefix
    }
    if ('suffix' in query) {
      suffix = query.suffix
      delete query.suffix
    }

    const base = super.urlForQuery(...args).replace(/\/assignments$/, '')
    let tail = '/assignments'
    if (typeof prefix === 'string' && prefix) tail += `/${prefix}`
    if (typeof suffix === 'string' && suffix) tail += `/${suffix}`

    return base + tail
  }

  /**
   * We need a custom updateRecord purely to use the PATCH verb instead of PUT when using RestAdapter/ActiveModelAdapter
   */
  updateRecord(...args: Parameters<ActiveModelAdapter['updateRecord']>) {
    const [_store, typeClass, snapshot] = args
    const serializer = new AssignmentSerializer(getOwner(this))
    let data = {}
    serializer.serializeIntoHash(data, typeClass, snapshot, {})

    // The update isn't really ActiveModelAdapter compliant, the endpoint just wants a hash of attributes
    // This is all that we support for updates
    if ('assignment' in data && data.assignment) {
      data = pickBy(data.assignment, (value, key) => value && ['start_at', 'end_at', 'expired_at'].includes(key))
    }

    const type = snapshot.modelName
    const { id } = snapshot
    assert('snapshot id for updating assignment record must be present', id !== null)
    const url = this.buildURL(type, id, snapshot, 'updateRecord')

    return this.ajax(url, 'PATCH', { data })
  }
}
